// contexts/NotificationContext.js
// ניהול התראות — polling כל 60 שניות
//
// ★ שדה ההודעה הוא alert_message (לא message!)
// ★ GET /alerts/unread-count → { data: { count: N } }
// ★ GET /alerts → data = array of alerts
// ★ PUT /alerts/{id}/read — סימון כנקרא
// ★ PUT /alerts/mark-all-read — סימון הכל כנקרא

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api, { extractErrorMessage } from '../services/api';
import { useAuth } from './AuthContext';

// =============================================================================
// Constants
// =============================================================================
const POLLING_INTERVAL = 60 * 1000; // 60 שניות
const RECENT_ALERTS_LIMIT = 5;

// =============================================================================
// Context
// =============================================================================
const NotificationContext = createContext(null);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}

// =============================================================================
// Provider
// =============================================================================
export function NotificationProvider({ children }) {
  const { isAuthenticated } = useAuth();

  const [unreadCount, setUnreadCount] = useState(0);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);

  const pollingRef = useRef(null);

  // ===================================================================
  // fetchUnreadCount — GET /alerts/unread-count
  // ===================================================================
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await api.get('/alerts/unread-count');
      if (response.data?.success) {
        setUnreadCount(response.data.data?.count || 0);
      }
    } catch (err) {
      // Polling failure — לא קריטי, ננסה שוב ב-interval הבא
      // 401 יטופל ב-api.js interceptor
    }
  }, [isAuthenticated]);

  // ===================================================================
  // fetchRecentAlerts — GET /alerts (is_read=0, limit=5)
  // נקרא כשהמשתמש פותח dropdown של התראות
  // ===================================================================
  const fetchRecentAlerts = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoadingAlerts(true);
    try {
      const response = await api.get('/alerts', {
        params: { is_read: 0 },
      });
      if (response.data?.success && Array.isArray(response.data.data)) {
        setRecentAlerts(response.data.data.slice(0, RECENT_ALERTS_LIMIT));
      }
    } catch (err) {
      // שגיאה בטעינת התראות — לא קריטי
    } finally {
      setIsLoadingAlerts(false);
    }
  }, [isAuthenticated]);

  // ===================================================================
  // markAsRead — PUT /alerts/{id}/read
  // ===================================================================
  const markAsRead = useCallback(async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/read`);
      // עדכון local state
      setRecentAlerts((prev) =>
        prev.map((alert) =>
          alert.id === alertId ? { ...alert, is_read: true } : alert
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      return { success: true };
    } catch (err) {
      return { success: false, error: extractErrorMessage(err) };
    }
  }, []);

  // ===================================================================
  // markAllRead — PUT /alerts/mark-all-read
  // ===================================================================
  const markAllRead = useCallback(async () => {
    try {
      await api.put('/alerts/mark-all-read');
      // עדכון local state
      setRecentAlerts((prev) =>
        prev.map((alert) => ({ ...alert, is_read: true }))
      );
      setUnreadCount(0);
      return { success: true };
    } catch (err) {
      return { success: false, error: extractErrorMessage(err) };
    }
  }, []);

  // ===================================================================
  // Polling — כל 60 שניות כשמחובר
  // ===================================================================
  useEffect(() => {
    if (isAuthenticated) {
      // קריאה ראשונית
      fetchUnreadCount();

      // התחלת polling
      pollingRef.current = setInterval(fetchUnreadCount, POLLING_INTERVAL);
    } else {
      // ניקוי כשלא מחובר
      setUnreadCount(0);
      setRecentAlerts([]);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isAuthenticated, fetchUnreadCount]);

  // ===================================================================
  // Test Harness — register notification data for getState()
  // ===================================================================
  useEffect(() => {
    if (window.__MADAD_TEST__?._setNotificationData) {
      window.__MADAD_TEST__._setNotificationData(unreadCount);
    }
  }, [unreadCount]);

  // ===================================================================
  // Context value
  // ===================================================================
  const value = {
    unreadCount,
    recentAlerts,
    isLoadingAlerts,
    fetchUnreadCount,
    fetchRecentAlerts,
    markAsRead,
    markAllRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export default NotificationContext;
